/**
 * DevAuthBanner Component (LOCAL-AUTH-1)
 * AIDLC Administrative Backend
 *
 * A red banner across the portal whenever sign-in is bypassed on either side:
 *   - the backend's AUTH_DEV_BYPASS is on for this computer (/api/health says devAuthBypass: true), or
 *   - this build skipped sign-in itself (VITE_DEV_AUTH_BYPASS=true).
 * Neither can be on without the banner showing. When only the build skips sign-in, the backend still asks for
 * one and every page fails, so the banner says that instead.
 */

import { useEffect, useState } from 'react';
import apiClient from '../../services/api';

const CLIENT_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
const HEALTH_PATH = '/health';

type ServerBypass = 'on' | 'off' | 'unknown';

/** What the backend says about its bypass; 'unknown' when it cannot be asked. */
function useServerBypass(): ServerBypass {
  const [state, setState] = useState<ServerBypass>('unknown');
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ devAuthBypass?: boolean }>(HEALTH_PATH)
      .then((res) => { if (!cancelled) setState(res.data?.devAuthBypass === true ? 'on' : 'off'); })
      .catch((err) => {
        console.error('[DevAuthBanner] Could not read /api/health:', err);
        if (!cancelled) setState('unknown');
      });
    return () => { cancelled = true; };
  }, []);
  return state;
}

function bannerText(server: ServerBypass): { title: string; detail: string } | null {
  if (server === 'on') {
    return {
      title: 'Sign-in is off on this computer (development mode).',
      detail: 'Anyone using this computer acts as an Admin, recorded as "unverified (dev mode)". To require sign-in, set AUTH_DEV_BYPASS=false in .env and restart the backend.',
    };
  }
  if (!CLIENT_BYPASS) return null;
  if (server === 'off') {
    return {
      title: 'This portal skipped sign-in, but the server requires it.',
      detail: 'Pages will not load. Set VITE_DEV_AUTH_BYPASS=false in .env, restart the portal and sign in.',
    };
  }
  return {
    title: 'This portal skipped sign-in (development mode).',
    detail: 'The server could not be reached to say whether it requires sign-in. Set VITE_DEV_AUTH_BYPASS=false in .env to sign in normally.',
  };
}

export function DevAuthBanner() {
  const text = bannerText(useServerBypass());
  if (!text) return null;
  return (
    <div role="alert" className="mb-4 rounded-md border border-red-700 bg-red-600 px-4 py-3 text-white break-words" data-testid="dev-auth-banner">
      <p className="text-sm font-semibold">{text.title}</p>
      <p className="mt-1 text-sm">{text.detail}</p>
    </div>
  );
}
